import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SegmentedControl, Text, Loader, Badge, Select } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
    IconCash,
    IconCreditCard,
    IconBuildingBank,
    IconBrandPaypal,
    IconBrandStripe,
    IconWallet
} from '@tabler/icons-react';
import { formatDay, formatDayLongTime, formatPrice } from '@/lib/format-utils';

import CardModal from '@/components/card-modal';
import { Reservation, Payment } from '@turizi/shared';
import { usePayments, queryKeys } from '@/hooks/api-hooks';
import { DEFAULT_CURRENCY } from '@turizi/shared';
import NewPaymentInModal from './new-payment-in-modal';
import { ReservationPaymentStatus } from './reservation-ui';
import SearchInput from './search-input';
import { MoneyNumberInput } from '@/components/snippets';

interface EditReservationPaymentProps {
    reservation?: Reservation;
    disabled?: boolean;
    onUpdate: (data: Partial<Reservation> & { payment_amount?: number; payment_id?: number | null }) => Promise<void> | void;
    opened?: boolean;
    onClose?: () => void;
    modalOnly?: boolean;
}

const getPaymentMethodIcon = (method: string) => {
    switch (method) {
        case 'cash':
            return <IconCash size={14} stroke={1.5} />;
        case 'card':
        case 'credit_debit_card':
            return <IconCreditCard size={14} stroke={1.5} />;
        case 'bank_transfer':
            return <IconBuildingBank size={14} stroke={1.5} />;
        case 'paypal':
            return <IconBrandPaypal size={14} stroke={1.5} />;
        case 'stripe':
            return <IconBrandStripe size={14} stroke={1.5} />;
        case 'wallet_transfer':
            return <IconWallet size={14} stroke={1.5} />;
        default:
            return <IconCash size={14} stroke={1.5} />;
    }
};

export default function EditReservationPayment({
    reservation,
    disabled,
    onUpdate,
    opened: controlledOpened,
    onClose: controlledOnClose,
    modalOnly = false
}: EditReservationPaymentProps) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [uncontrolledOpened, { open, close: uncontrolledClose }] = useDisclosure(false);
    const [activeTab, setActiveTab] = useState<'link' | 'new'>('new');
    const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
    const [linkAmount, setLinkAmount] = useState<number | null>(null);
    const [searchText, setSearchText] = useState('');
    const [linkPaymentStatus, setLinkPaymentStatus] = useState<string>('paid');

    const isControlled = controlledOpened !== undefined;
    const opened = isControlled ? controlledOpened : uncontrolledOpened;
    const close = isControlled ? (controlledOnClose || (() => { })) : uncontrolledClose;

    // Fetch payments of this reservation's client
    const clientParams = useMemo(() => {
        if (!reservation) return null;
        if (reservation.client_id_business) return { business_id: reservation.client_id_business, limit: 100 };
        return null;
    }, [reservation]);

    const {
        data: paymentsResponse,
        isLoading: isLoadingPayments
    } = usePayments(clientParams, {
        enabled: opened && !!clientParams
    });

    const clientPayments = paymentsResponse?.data || [];

    const filteredPayments = useMemo(() => {
        if (!searchText) return clientPayments;
        const searchLower = searchText.toLowerCase();
        return clientPayments.filter(p => {
            const method = t(p.payment_method || '').toLowerCase();
            const notes = (p.notes || '').toLowerCase();
            const amount = String(p.amount);
            const id = String(p.id);
            const currency = (p.currency || '').toLowerCase();
            const date = p.payment_date ? dayjs(p.payment_date).format('YYYY-MM-DD') : '';
            const dueDate = p.due_date ? dayjs(p.due_date).format('YYYY-MM-DD') : '';

            return method.includes(searchLower) ||
                notes.includes(searchLower) ||
                amount.includes(searchLower) ||
                id.includes(searchLower) ||
                currency.includes(searchLower) ||
                date.includes(searchLower) ||
                dueDate.includes(searchLower);
        });
    }, [clientPayments, searchText, t]);

    // Calculate total price and currency
    const reservationTotals = useMemo(() => {
        if (!reservation) return { amount: 0, currency: DEFAULT_CURRENCY };
        const totals = reservation.price_per_currency || {};
        const currency = Object.keys(totals)[0] || DEFAULT_CURRENCY;
        const amount = totals[currency] || 0;
        return { amount, currency };
    }, [reservation?.price_per_currency]);

    const selectedPayment = useMemo(() => {
        if (!selectedPaymentId) return null;
        return clientPayments.find(p => String(p.id) === selectedPaymentId) || null;
    }, [selectedPaymentId, clientPayments]);

    const handleSelectPayment = (p: Payment) => {
        if (selectedPaymentId === String(p.id)) return;
        setSelectedPaymentId(String(p.id));

        const owed = reservation?.payment_owed_per_currency?.[p.currency || ''];
        let defaultAmt = Number(p.amount || 0);
        if (owed !== undefined && owed !== null && Number(owed) > 0) {
            defaultAmt = Math.min(Number(p.amount || 0), Number(owed));
        } else if (reservationTotals.currency === p.currency && Number(reservationTotals.amount) > 0) {
            defaultAmt = Math.min(Number(p.amount || 0), Number(reservationTotals.amount));
        }
        setLinkAmount(defaultAmt);
    };

    const handleTabChange = (value: 'link' | 'new') => {
        setActiveTab(value);
        setSelectedPaymentId(null);
        setLinkAmount(null);
    };

    useEffect(() => {
        if (!opened) {
            setSelectedPaymentId(null);
            setLinkAmount(null);
            setSearchText('');
        }
    }, [opened]);

    const isAmountInvalid = !selectedPaymentId || linkAmount === null || linkAmount === undefined || linkAmount <= 0;

    const renderPaymentCard = (p: Payment) => {
        const isOverdue = !p.payment_date && p.due_date && dayjs(p.due_date).isBefore(dayjs(), 'day');

        return (
            <div className="flex items-center justify-between w-full py-0.5 text-xs">
                <div className="flex items-center gap-3">
                    <div className="text-[10px]  text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                        #{p.id}
                    </div>
                    <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5 text-xs text-gray-800">
                            {getPaymentMethodIcon(p.payment_method || '')}
                            <span className="font-semibold">{t(p.payment_method || 'payment_method')}</span>
                        </div>
                        <div className="text-xs text-gray-500 flex gap-2">
                            <span>{t('created')}: {formatDayLongTime(p.created_at)}</span>
                            <span>{t('paid_on')}: {formatDay(p.payment_date)}</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2  font-bold text-sm text-emerald-600">
                    <span>${formatPrice(p.amount)}</span>
                    <span className="text-[10px] text-gray-400 font-semibold">{p.currency}</span>
                </div>
            </div>
        );
    };

    if (!reservation) return null;

    const handleLinkPayment = async () => {
        if (!selectedPaymentId || isAmountInvalid) return;
        await onUpdate({
            payment_status: linkPaymentStatus,
            payment_id: Number(selectedPaymentId),
            payment_amount: Number(linkAmount)
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservation.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
        close();
    };

    const handleNewPaymentSuccess = (payment: Payment) => {
        // Invalidate reservation details so the updated status is displayed on the page
        queryClient.invalidateQueries({ queryKey: queryKeys.reservations.detail(reservation.id) });
        queryClient.invalidateQueries({ queryKey: queryKeys.reservations.all });
        close();
    };

    const client = reservation.client_business;
    const clientName = client?.name || t('client');

    return (
        <>
            {!modalOnly && (
                <div className='flex gap-2'>
                    {reservation.payment_status === 'paid' ? (
                        <div
                            onClick={() => onUpdate({ payment_status: 'unpaid', payment_id: null })}
                        >
                            {t('unlink_payment')}
                        </div>
                    ) : (
                        <div
                            onClick={() => {
                                setSelectedPaymentId(null);
                                setLinkAmount(null);
                                setActiveTab('link');
                                open();
                            }}
                        >
                            {t('enter_payment')}
                        </div>
                    )}
                    <ReservationPaymentStatus reservation={reservation} />
                </div>
            )}

            <CardModal
                size="full"
                opened={opened}
                onClose={close}
                header={(
                    <div className="flex gap-2">
                        <div className="text-lg font-semibold">
                            {clientName}
                        </div>
                        <div className="text-lg">
                            <span className='text-gray-500'>{t('reservation')}</span> #{reservation.id}
                        </div>
                    </div>
                )}
            >
                <div className="flex flex-col gap-4">
                    <SegmentedControl
                        value={activeTab}
                        onChange={(value) => handleTabChange(value as 'link' | 'new')}
                        data={[
                            { label: t('create_new_payment'), value: 'new' },
                            { label: t('link_existing_payment'), value: 'link' },
                        ]}
                        fullWidth
                        size="md"
                        radius="xl"
                        color='black'
                    />

                    {activeTab === 'link' ? (
                        <div className="flex flex-col gap-4">
                            <SearchInput
                                onSearch={setSearchText}
                                placeholder={t('search_payments') + '...'}
                                size="sm"
                            />
                            {isLoadingPayments ? (
                                <div className="flex justify-center p-4">
                                    <Loader size="sm" />
                                </div>
                            ) : filteredPayments.length === 0 ? (
                                <div className="text-center py-6 text-sm text-gray-400 italic">
                                    {t('no_payments_found_for_this_client')}
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2 max-h-[300px] overflow-y-auto pr-1">
                                    {filteredPayments.map(p => {
                                        const isSelected = selectedPaymentId === String(p.id);
                                        return (
                                            <div
                                                key={p.id}
                                                className={`p-3 border rounded-xl cursor-pointer transition-all ${isSelected
                                                    ? 'border-emerald-500 bg-emerald-50/20 shadow-sm ring-1 ring-emerald-500/20'
                                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
                                                    }`}
                                                onClick={() => handleSelectPayment(p)}
                                            >
                                                {renderPaymentCard(p)}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {selectedPayment && (
                                <div className="p-3 bg-slate-50/90 border border-slate-200 rounded-xl flex items-center justify-between gap-4">
                                    <div className="flex flex-col gap-0.5">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-semibold text-gray-800">
                                                {t('amount_to_link')}
                                            </span>
                                            <Badge color="gray" variant="light" size="xs">
                                                #{selectedPayment.id}
                                            </Badge>
                                        </div>
                                        <span className="text-[11px] text-gray-500">
                                            {t('total_amount')}: ${formatPrice(selectedPayment.amount)} {selectedPayment.currency}
                                        </span>
                                    </div>
                                    <div className="w-44">
                                        <MoneyNumberInput
                                            size="sm"
                                            amount={linkAmount}
                                            onChange={(val) => setLinkAmount(val)}
                                            rightSection={
                                                <span className="text-[11px] text-gray-400 font-semibold pr-2">
                                                    {selectedPayment.currency}
                                                </span>
                                            }
                                            error={isAmountInvalid ? t('amount_must_be_greater_than_zero') : undefined}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end gap-2 mt-2">
                                <Button variant="subtle" size="xs" color="gray" onClick={close}>
                                    {t('cancel')}
                                </Button>
                                <Button
                                    size="xs"
                                    color="black"
                                    disabled={!selectedPaymentId || isAmountInvalid}
                                    onClick={handleLinkPayment}
                                >
                                    {t('link_payment')}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-2">
                            <NewPaymentInModal
                                onClose={close}
                                initialFromClient={client}
                                initialAffectedReservations={[reservation.id]}
                                initialAmount={reservationTotals.amount}
                                initialCurrency={reservationTotals.currency}
                                onSuccess={handleNewPaymentSuccess}
                            />
                        </div>
                    )}
                </div>
            </CardModal>
        </>
    );
}
